import { useEffect, useState } from 'react';
import { Box, IconButton, Link, Typography } from '@mui/material';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthLayout, SubmitButton } from '../components';
import { getApiScopes } from '../auth/msalConfig';
import { clearSignedOut, peekSignedOut, useAuthStatus } from '../auth/authStatus';
import { SignInBlockedPage } from './SignInBlockedPage';

/**
 * Post-logout / sign-in-failed landing (Prompt A1 / `Sign In.html`
 * `SignInCard`). PUBLIC, neutral DecisionPoint brand, rendered OUTSIDE the
 * shell. No email/password field (credentials are entered on the
 * Microsoft-hosted page after the redirect) and no pre-auth language selector.
 * "Sign in" hands off to MSAL `loginRedirect` and swaps to a "Redirecting…"
 * pending state.
 *
 * This is **no longer the cold-start landing**: since DDR-PLT-024 an
 * unauthenticated visit to any guarded route goes straight to Microsoft via
 * `RequireAuth`. The page is retained because it must stay public and
 * NON-redirecting — it is the registered `postLogoutRedirectUri`, and it is the
 * only surface that can confirm a sign-out worked. If it auto-redirected, a
 * sign-out would either dump the user on Microsoft's credential page with no
 * confirmation, or (with a live Entra session in another tab, or workforce SSO)
 * silently re-authenticate them and look like it did nothing at all. It is also
 * the loop-breaker for the blocked-account dead end, whose exit runs
 * `logoutRedirect` and lands here.
 *
 * Three states off one public route:
 *  - `signInFailed` (the bootstrap caught an MSAL interactive failure) → the
 *    A3 "sign-in didn't complete" card with a working "Try again".
 *  - just signed out (the logout redirect landed here) → the landing plus a
 *    dismissible left-accent "You've been signed out." banner above the card.
 *  - default → the landing card.
 */
export function SignInPage() {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { signInFailed } = useAuthStatus();
  const [pending, setPending] = useState(false);
  const [showSignedOut, setShowSignedOut] = useState(() => peekSignedOut());

  // Consume the one-shot signed-out hint after capturing it, so it doesn't
  // re-appear on a later visit to the landing. Idempotent under StrictMode.
  useEffect(() => {
    clearSignedOut();
  }, []);

  if (signInFailed) {
    return <SignInBlockedPage reason="signInFailed" />;
  }

  // Already signed in (an explicit sign-in from this page returned here now
  // that `navigateToLoginRequestUrl` is `true`, or a direct visit with a live
  // session): forward to the index, which resolves the real workspace via
  // `whoami`, instead of stranding the user on the public landing.
  //
  // There is no `location.state.from` to honour any more. `RequireAuth` used to
  // pass the requested path here in router state, but that state never survived
  // the cross-origin hop to Entra — it was dead code for the unauthenticated
  // flow. Deep links are now preserved by MSAL restoring the login-request URL.
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handleSignIn = () => {
    setPending(true);
    void instance.loginRedirect({ scopes: getApiScopes() });
  };

  const signedOutBanner = showSignedOut ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        mb: '20px',
        p: '10px 12px',
        bgcolor: '#EAF1F8',
        border: '1px solid rgba(0, 71, 133, 0.20)',
        borderLeft: '3px solid',
        borderLeftColor: 'primary.main',
        borderRadius: '4px',
      }}
    >
      <CheckCircleOutlined sx={{ fontSize: 17, color: 'primary.main' }} />
      <Typography variant="body2" sx={{ flex: 1, color: 'text.primary' }}>
        {t('signedOut.banner')}
      </Typography>
      <IconButton
        size="small"
        onClick={() => setShowSignedOut(false)}
        aria-label={t('actions.close')}
        sx={{ color: 'text.secondary' }}
      >
        <CloseOutlined fontSize="small" />
      </IconButton>
    </Box>
  ) : undefined;

  return (
    <AuthLayout
      bannerAbove={signedOutBanner}
      footer={
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.25 }}>
          <Link
            href="#"
            onClick={(event) => event.preventDefault()}
            sx={{ color: 'text.secondary', fontSize: 12 }}
          >
            {t('signIn.footerSupport')}
          </Link>
          <Box component="span" sx={{ color: '#CDD3E0' }}>
            ·
          </Box>
          <Box component="span" sx={{ fontSize: 12 }}>
            {t('shell.brandFallback')}
          </Box>
        </Box>
      }
    >
      <Box sx={{ textAlign: 'center', mb: '28px' }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          {t('app.name')}
        </Typography>
        <Typography variant="body1" sx={{ mt: '6px', color: 'text.secondary' }}>
          {t('signIn.subtitle')}
        </Typography>
      </Box>
      <SubmitButton
        type="button"
        fullWidth
        pending={pending}
        pendingLabel={t('signIn.redirecting')}
        onClick={handleSignIn}
        sx={{ minHeight: 44 }}
      >
        {t('signIn.action')}
      </SubmitButton>
      <Typography
        variant="caption"
        sx={{ display: 'block', mt: '12px', textAlign: 'center', color: 'text.secondary' }}
      >
        {t('signIn.redirectHelper')}
      </Typography>
    </AuthLayout>
  );
}
